import React, { useEffect, useRef } from 'react'
import qs from 'querystring'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Alert, Modal, Progress, Tag } from 'antd'
import { useStore } from '@'

export const RESOURCE = 'hotel-mapping'
export const DEFAULT_PAGE_SIZE = 50
export const MAX_IDS = 5000
export const HOTELS_PATH = '/hotel/mapping-coverage/hotels'
export const REGIONS_PATH = '/hotel/mapping-coverage/regions'

// hotel_mapping_v2.supplier_code only labels where a row came from; search reads a region by region_id alone.
export const REGION_SOURCES = ['EPS', 'RateHawk']

export const REGION_TYPES = [
    'city',
    'province_state',
    'high_level_region',
    'multi_city_vicinity',
    'neighborhood',
    'country',
    'airport'
]

export const text = value => (value === undefined || value === null ? '' : `${value}`.trim())

export const parseIds = value => {
    const ids = text(value)
        .split(/[\s,;]+/)
        .filter(Boolean)
    return Array.from(new Set(ids))
}

export const codeTags = codes =>
    codes && codes.length ? codes.map(code => <Tag key={code}>{code}</Tag>) : <span style={{ color: '#999' }}>—</span>

export const ErrorBox = ({ result, description }) => (
    <Alert type="error" showIcon message={result.message || 'Request failed'} description={description} />
)

export const HotelLink = ({ id }) => <Link to={`${HOTELS_PATH}?${qs.stringify({ hotel_id: id })}`}>{id}</Link>

export const RegionLink = ({ id }) => <Link to={`${REGIONS_PATH}?${qs.stringify({ region_id: id })}`}>{id}</Link>

// Modal.confirm keeps itself open on a rejected onOk and leaves the rejection unhandled; the action already
// reported the error, so settle here.
export const confirmDanger = ({ title, content, okText, onOk }) =>
    Modal.confirm({
        title,
        content,
        okText,
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => onOk().catch(console.error)
    })

export const supplierColumns = [
    {
        key: 'status',
        dataIndex: 'status',
        title: 'Status',
        width: 110,
        render: status => (status === 'mapped' ? <Tag color="green">mapped</Tag> : <Tag color="red">missing</Tag>)
    },
    { key: 'column', dataIndex: 'column', title: 'Catalog' },
    { key: 'codes', dataIndex: 'codes', title: 'Supplier codes', render: codeTags },
    { key: 'supplier_hotel_id', dataIndex: 'supplier_hotel_id', title: 'Supplier hotel id', render: v => v || '' }
]

export const coverageColumns = [
    { key: 'column', dataIndex: 'column', title: 'Catalog' },
    { key: 'codes', dataIndex: 'codes', title: 'Supplier codes', render: codeTags },
    { key: 'mapped', dataIndex: 'mapped', title: 'Mapped', width: 110, sorter: (a, b) => a.mapped - b.mapped },
    {
        key: 'percent',
        dataIndex: 'percent',
        title: 'Coverage',
        width: 240,
        sorter: (a, b) => a.percent - b.percent,
        render: percent => <Progress percent={percent} size="small" format={v => `${v}%`} />
    }
]

const pickQuery = (source, fields) => {
    const out = {}
    fields.forEach(field => {
        const value = text(source && source[field])
        if (value) {
            out[field] = value
        }
    })
    return out
}

const sameQuery = (a, b, fields) => fields.every(field => (a[field] || '') === (b[field] || ''))

// The address bar and the query store follow each other, so what is on screen can be shared as a link.
// `fields` and `load` must be stable (module level); fields[0] is the one that makes a query.
export const useUrlQuery = ($query, fields, load) => {
    const location = useLocation()
    const history = useHistory()
    const [query] = useStore([$query])
    const mounted = useRef(false)

    useEffect(() => {
        const fromUrl = pickQuery(qs.parse(location.search.replace(/^\?/, '')), fields)
        const current = $query.getState()
        if (fromUrl[fields[0]]) {
            if (!mounted.current || !current || !sameQuery(fromUrl, pickQuery(current, fields), fields)) {
                load(fromUrl)
            }
        } else if (!mounted.current && current) {
            load(current)
        }
        mounted.current = true
    }, [$query, fields, load, location.search])

    useEffect(() => {
        // Read the store, not `query`: a load started by the effect above in this same commit is newer.
        const current = $query.getState()
        if (!current) {
            return
        }
        const search = `?${qs.stringify(pickQuery(current, fields))}`
        if (search !== location.search) {
            history.replace({ pathname: location.pathname, search })
        }
    }, [$query, fields, history, location.pathname, location.search, query])
}
